import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils.ts'

const buttonStyles = cva(
  'inline-flex items-center justify-center font-medium rounded-md transition focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 disabled:cursor-not-allowed whitespace-nowrap',
  {
    variants: {
      variant: {
        primary:
          'bg-brand-700 text-white hover:bg-brand-800 focus-visible:ring-brand-500 shadow-sm disabled:bg-gray-200 disabled:text-gray-500 disabled:shadow-none',
        secondary:
          'bg-white text-gray-900 border border-gray-300 hover:bg-gray-50 focus-visible:ring-gray-400 disabled:bg-gray-50 disabled:text-gray-400 disabled:border-gray-200',
        ghost:
          'bg-transparent text-gray-700 hover:bg-gray-100 focus-visible:ring-gray-400 disabled:text-gray-400 disabled:hover:bg-transparent',
        danger:
          'bg-red-600 text-white hover:bg-red-700 focus-visible:ring-red-500 shadow-sm disabled:bg-gray-200 disabled:text-gray-500 disabled:shadow-none',
        accent:
          'bg-accent-500 text-white hover:bg-accent-600 focus-visible:ring-accent-500 shadow-sm disabled:bg-gray-200 disabled:text-gray-500 disabled:shadow-none',
        link:
          'bg-transparent text-brand-700 hover:text-brand-800 hover:underline p-0 h-auto focus-visible:ring-brand-400 disabled:text-gray-400 disabled:no-underline',
        linkDanger:
          'bg-transparent text-red-600 hover:text-red-700 hover:underline p-0 h-auto focus-visible:ring-red-400 disabled:text-gray-400 disabled:no-underline',
      },
      size: {
        xs: 'text-caption px-2 py-1 gap-1',
        sm: 'text-small px-2.5 py-1.5 gap-1.5',
        md: 'text-body px-3.5 py-2 gap-2',
        lg: 'text-body-lg px-4 py-2.5 gap-2',
      },
      fullWidth: {
        true: 'w-full',
      },
    },
    compoundVariants: [
      { variant: 'link', size: 'xs', class: 'px-0 py-0' },
      { variant: 'link', size: 'sm', class: 'px-0 py-0' },
      { variant: 'link', size: 'md', class: 'px-0 py-0' },
      { variant: 'link', size: 'lg', class: 'px-0 py-0' },
      { variant: 'linkDanger', size: 'xs', class: 'px-0 py-0' },
      { variant: 'linkDanger', size: 'sm', class: 'px-0 py-0' },
      { variant: 'linkDanger', size: 'md', class: 'px-0 py-0' },
      { variant: 'linkDanger', size: 'lg', class: 'px-0 py-0' },
    ],
    defaultVariants: {
      variant: 'primary',
      size: 'md',
    },
  },
)

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonStyles> {
  loading?: boolean
  leadingIcon?: ReactNode
  trailingIcon?: ReactNode
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      fullWidth,
      loading,
      disabled,
      leadingIcon,
      trailingIcon,
      children,
      type = 'button',
      ...props
    },
    ref,
  ) => (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      className={cn(buttonStyles({ variant, size, fullWidth }), className)}
      {...props}
    >
      {loading ? (
        <Loader2 className="h-4 w-4 animate-spin" />
      ) : (
        leadingIcon
      )}
      {children}
      {!loading && trailingIcon}
    </button>
  ),
)
Button.displayName = 'Button'
